import type { Course, Enrollment } from "./types";

const DAY_MS = 86_400_000;

/**
 * Expiry timestamp (ms) for a user's access to a course, or `null` when access
 * never expires (course has no duration, or the registration date is unknown).
 */
export function accessExpiry(
  enrolledAt: number | undefined,
  durationDays: number | undefined,
): number | null {
  if (!durationDays || durationDays <= 0) return null; // permanent
  if (!enrolledAt) return null; // no registration date -> don't expire
  return enrolledAt + durationDays * DAY_MS;
}

/** True if the user currently has active (non-expired) access to the course. */
export function isAccessActive(
  enrollment: Pick<Enrollment, "courses" | "enrolledAt"> | null | undefined,
  course: Pick<Course, "id" | "accessDurationDays">,
  now: number = Date.now(),
): boolean {
  if (!enrollment?.courses?.[course.id]) return false;
  const expiry = accessExpiry(
    enrollment.enrolledAt?.[course.id],
    course.accessDurationDays,
  );
  return expiry === null || now <= expiry;
}

/** True if the user had access but it has lapsed. */
export function isAccessExpired(
  enrollment: Pick<Enrollment, "courses" | "enrolledAt"> | null | undefined,
  course: Pick<Course, "id" | "accessDurationDays">,
  now: number = Date.now(),
): boolean {
  if (!enrollment?.courses?.[course.id]) return false;
  const expiry = accessExpiry(
    enrollment.enrolledAt?.[course.id],
    course.accessDurationDays,
  );
  return expiry !== null && now > expiry;
}
